import { useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/PageHeader";
import {
  deleteSubscribersFn,
  importSubscribersFn,
  listSubscribersFn,
  saveSubscriberFn,
  subscriberProfileFn,
  updateSubscribersFn,
} from "@/lib/newsletter/newsletter.functions";
import {
  SOURCE_LABEL,
  type BlogSubscriber,
  type ImportSummary,
  type SubscriberInput,
  type SubscriberStatus,
} from "@/lib/newsletter/types";
import {
  downloadImportTemplate,
  exportSubscribers,
  readFileAsBase64,
} from "@/lib/newsletter/xlsx";

export const Route = createFileRoute("/admin/subscribers")({ component: SubscribersPage });

const CARD = "border border-black/10 bg-white p-4";
const LABEL = "text-[10px] font-semibold uppercase tracking-[0.2em] text-black/45";
const BTN = "border border-black/20 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em]";
const FIELD = "w-full border border-black/15 px-3 py-2 text-sm focus:border-black/50 focus:outline-none";

const STATUS_LABEL: Record<SubscriberStatus, string> = {
  active: "Active",
  unsubscribed: "Unsubscribed",
  bounced: "Bounced",
};

const BLANK: SubscriberInput = {
  email: "",
  firstName: "",
  lastName: "",
  phone: "",
  city: "",
  state: "",
  country: "India",
  company: "",
  tags: [],
  notes: "",
  status: "active",
  source: "manual",
};

function fmt(v: string | null) {
  if (!v) return "—";
  return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function Panel({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" role="dialog" aria-modal="true">
      <div className="h-full w-full max-w-xl overflow-y-auto bg-white p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold tracking-tight text-black">{title}</h2>
          <button type="button" className={BTN} onClick={onClose}>Close</button>
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function SubscribersPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["newsletter", "subscribers"],
    queryFn: () => listSubscribersFn(),
  });

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"all" | SubscriberStatus>("all");
  const [source, setSource] = useState("all");
  const [tag, setTag] = useState("all");
  const [selected, setSelected] = useState<string[]>([]);
  const [editing, setEditing] = useState<SubscriberInput | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["newsletter"] });

  const update = useMutation({
    mutationFn: (input: { ids: string[]; status: SubscriberStatus }) => updateSubscribersFn({ data: input }),
    onSuccess: () => { void invalidate(); setSelected([]); toast.success("Subscribers updated"); },
    onError: (e: Error) => toast.error(e.message || "Update failed"),
  });

  const remove = useMutation({
    mutationFn: (ids: string[]) => deleteSubscribersFn({ data: { ids } }),
    onSuccess: () => { void invalidate(); setSelected([]); toast.success("Subscribers deleted"); },
    onError: (e: Error) => toast.error(e.message || "Delete failed"),
  });

  const save = useMutation({
    mutationFn: (input: SubscriberInput) => saveSubscriberFn({ data: input }),
    onSuccess: (r) => {
      void invalidate();
      setEditing(null);
      toast.success(r.created ? "Subscriber added" : "Subscriber updated");
    },
    onError: (e: Error) => toast.error(e.message || "Could not save the subscriber"),
  });

  const detailQuery = useQuery({
    queryKey: ["newsletter", "subscriber", detailId],
    queryFn: () => subscriberProfileFn({ data: { id: detailId as string } }),
    enabled: Boolean(detailId),
  });

  const all = query.data?.subscribers ?? [];
  const stats = query.data?.stats;
  const provider = query.data?.provider;
  const campaigns = query.data?.campaigns ?? [];
  const allTags = useMemo(() => [...new Set(all.flatMap((s) => s.tags))].sort(), [all]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((s) => {
      if (status !== "all" && s.status !== status) return false;
      if (source !== "all" && s.source !== source) return false;
      if (tag !== "all" && !s.tags.includes(tag)) return false;
      if (!q) return true;
      return [s.email, s.fullName, s.firstName, s.lastName, s.phone, s.company, s.city, s.tags.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [all, search, status, source, tag]);

  async function onFile(file: File) {
    setUploading(true);
    setImportSummary(null);
    try {
      const base64 = await readFileAsBase64(file);
      const summary = await importSubscribersFn({ data: { fileName: file.name, base64 } });
      setImportSummary(summary);
      void invalidate();
      toast.success(`${summary.imported} added · ${summary.updated} updated`);
    } catch (e) {
      toast.error((e as Error).message || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const toEdit = (s: BlogSubscriber): SubscriberInput => ({
    id: s.id, email: s.email, firstName: s.firstName, lastName: s.lastName, fullName: s.fullName,
    phone: s.phone, city: s.city, state: s.state, country: s.country, company: s.company,
    tags: s.tags, notes: s.notes, status: s.status, source: s.source,
  });

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Email subscribers"
        subtitle="Marketing audience — registrations, opt-ins and imported contacts"
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" className={BTN} onClick={() => setImportOpen(true)}>Import</button>
            <button type="button" className={BTN} onClick={() => exportSubscribers(rows, "filtered")}>
              Export
            </button>
            <button type="button" className={`${BTN} bg-black text-white`} onClick={() => setEditing(BLANK)}>
              Add subscriber
            </button>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
        {[
          ["Total", stats?.total ?? 0],
          ["Active", stats?.active ?? 0],
          ["Unsubscribed", stats?.unsubscribed ?? 0],
          ["Bounced", stats?.bounced ?? 0],
          ["New this month", stats?.newThisMonth ?? 0],
          ["Imported today", stats?.importedToday ?? 0],
        ].map(([label, value]) => (
          <div key={String(label)} className={CARD}>
            <p className={LABEL}>{label}</p>
            <p className="mt-1 text-2xl text-black">{String(value)}</p>
          </div>
        ))}
      </div>

      <div className={CARD}>
        <p className={LABEL}>Email provider</p>
        <p className="mt-1 text-sm text-black/80">
          {provider?.activeLabel
            ? `${provider.activeLabel}${provider.configured ? "" : " (credentials missing)"}`
            : "No provider configured — campaigns will queue and stay unsent."}
        </p>
        <p className="mt-1 text-[11px] text-black/45">
          Available adapters:{" "}
          {(provider?.available ?? []).map((p) => `${p.label}${p.configured ? " ✓" : ""}`).join(" · ")}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          className="min-w-[220px] flex-1 border border-black/15 px-3 py-2 text-sm"
          placeholder="Search email, name, phone, company or tag"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="border border-black/15 px-3 py-2 text-sm" value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="unsubscribed">Unsubscribed</option>
          <option value="bounced">Bounced</option>
        </select>
        <select className="border border-black/15 px-3 py-2 text-sm" value={source}
          onChange={(e) => setSource(e.target.value)}>
          <option value="all">All sources</option>
          {Object.keys(SOURCE_LABEL).map((k) => (
            <option key={k} value={k}>{SOURCE_LABEL[k as keyof typeof SOURCE_LABEL]}</option>
          ))}
        </select>
        <select className="border border-black/15 px-3 py-2 text-sm" value={tag}
          onChange={(e) => setTag(e.target.value)}>
          <option value="all">All tags</option>
          {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border border-[#C6A15B]/40 bg-[#C6A15B]/5 p-3">
          <span className="text-[12px] text-black/70">{selected.length} selected</span>
          <button type="button" className={BTN} onClick={() => update.mutate({ ids: selected, status: "active" })}>
            Mark active
          </button>
          <button type="button" className={BTN} onClick={() => update.mutate({ ids: selected, status: "unsubscribed" })}>
            Deactivate
          </button>
          <button type="button" className={BTN} onClick={() => update.mutate({ ids: selected, status: "bounced" })}>
            Mark bounced
          </button>
          <button
            type="button"
            className={BTN}
            onClick={() => exportSubscribers(all.filter((s) => selected.includes(s.id)), "selected")}
          >
            Export selected
          </button>
          <button
            type="button"
            className="border border-red-300 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-red-700"
            onClick={() => {
              if (confirm(`Delete ${selected.length} subscriber(s) permanently?`)) remove.mutate(selected);
            }}
          >
            Delete
          </button>
        </div>
      )}

      <div className="overflow-x-auto border border-black/10 bg-white">
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead className="border-b border-black/10 text-[10px] uppercase tracking-[0.18em] text-black/45">
            <tr>
              <th className="p-3">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && selected.length === rows.length}
                  onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.id) : [])}
                />
              </th>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Location</th>
              <th className="p-3">Status</th>
              <th className="p-3">Source</th>
              <th className="p-3">Subscribed</th>
              <th className="p-3">Last email</th>
              <th className="p-3">Tags</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading && (
              <tr><td colSpan={11} className="p-6 text-center text-black/50">Loading subscribers…</td></tr>
            )}
            {!query.isLoading && rows.length === 0 && (
              <tr><td colSpan={11} className="p-6 text-center text-black/50">No subscribers match these filters.</td></tr>
            )}
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-black/5">
                <td className="p-3">
                  <input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggle(s.id)} />
                </td>
                <td className="p-3 text-black">
                  {s.fullName || [s.firstName, s.lastName].filter(Boolean).join(" ") || "—"}
                  {s.company && <span className="block text-[11px] text-black/45">{s.company}</span>}
                </td>
                <td className="p-3 text-black/80">{s.email}</td>
                <td className="p-3 text-black/60">{s.phone || "—"}</td>
                <td className="p-3 text-black/60">
                  {[s.city, s.state, s.country].filter(Boolean).join(", ") || "—"}
                </td>
                <td className="p-3">
                  <span
                    className={
                      "border px-2 py-1 text-[10px] uppercase tracking-[0.14em] " +
                      (s.status === "active"
                        ? "border-emerald-300 text-emerald-700"
                        : s.status === "bounced"
                          ? "border-red-300 text-red-700"
                          : "border-black/20 text-black/50")
                    }
                  >
                    {STATUS_LABEL[s.status]}
                  </span>
                </td>
                <td className="p-3 text-black/60">{SOURCE_LABEL[s.source] ?? s.source}</td>
                <td className="p-3 text-black/70">{fmt(s.subscribedAt)}</td>
                <td className="p-3 text-black/70">
                  {fmt(s.lastEmailSent)}
                  <span className="block text-[11px] text-black/40">{s.totalEmailsSent} sent</span>
                </td>
                <td className="p-3 text-black/60">{s.tags.join(", ") || "—"}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    <button type="button" className={BTN} onClick={() => setDetailId(s.id)}>View</button>
                    <button type="button" className={BTN} onClick={() => setEditing(toEdit(s))}>Edit</button>
                    {s.status === "active" ? (
                      <button type="button" className={BTN}
                        onClick={() => update.mutate({ ids: [s.id], status: "unsubscribed" })}>
                        Deactivate
                      </button>
                    ) : (
                      <button type="button" className={BTN}
                        onClick={() => update.mutate({ ids: [s.id], status: "active" })}>
                        Reactivate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className={CARD}>
        <p className={LABEL}>Recent campaigns</p>
        {campaigns.length === 0 ? (
          <p className="mt-2 text-sm text-black/50">No newsletter campaigns yet.</p>
        ) : (
          <ul className="mt-2 flex flex-col gap-2">
            {campaigns.slice(0, 10).map((c) => (
              <li key={c.id} className="flex flex-wrap justify-between gap-2 border-b border-black/5 pb-2 text-[13px]">
                <span className="text-black">{c.subject}</span>
                <span className="text-black/50">
                  {c.status} · {c.sentCount}/{c.recipientsTotal} sent
                  {c.failedCount ? ` · ${c.failedCount} failed` : ""}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {importOpen && (
        <Panel title="Import subscribers" onClose={() => { setImportOpen(false); setImportSummary(null); }}>
          <div className="space-y-4 text-sm text-black/70">
            <p>
              Upload a CSV or XLSX file. Email is mandatory; every other column is optional. Existing
              addresses are enriched in place — duplicates are never created.
            </p>
            <button type="button" className={BTN} onClick={downloadImportTemplate}>
              Download template
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className={FIELD}
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void onFile(file);
              }}
            />
            {uploading && <p className="text-black/60">Uploading and validating…</p>}
            {importSummary && (
              <div className="border border-black/10 p-3">
                <p className={LABEL}>Import summary</p>
                <ul className="mt-2 space-y-1">
                  <li>Rows read: {importSummary.total}</li>
                  <li>Added: {importSummary.imported}</li>
                  <li>Updated: {importSummary.updated}</li>
                  <li>Skipped duplicates: {importSummary.skipped}</li>
                  <li>Invalid rows: {importSummary.invalid}</li>
                </ul>
                {importSummary.errors.length > 0 && (
                  <ul className="mt-2 space-y-1 text-[12px] text-red-700">
                    {importSummary.errors.map((err) => <li key={err}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </Panel>
      )}

      {editing && (
        <Panel title={editing.id ? "Edit subscriber" : "Add subscriber"} onClose={() => setEditing(null)}>
          <form
            className="space-y-3"
            onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }}
          >
            {([
              ["email", "Email", true],
              ["firstName", "First name", false],
              ["lastName", "Last name", false],
              ["phone", "Phone", false],
              ["company", "Company", false],
              ["city", "City", false],
              ["state", "State", false],
              ["country", "Country", false],
            ] as const).map(([key, label, required]) => (
              <label key={key} className="block">
                <span className={LABEL}>{label}</span>
                <input
                  className={FIELD}
                  required={required}
                  value={(editing[key] as string) ?? ""}
                  onChange={(e) => setEditing({ ...editing, [key]: e.target.value })}
                />
              </label>
            ))}
            <label className="block">
              <span className={LABEL}>Tags (comma separated)</span>
              <input
                className={FIELD}
                value={(editing.tags ?? []).join(", ")}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                  })
                }
              />
            </label>
            <label className="block">
              <span className={LABEL}>Status</span>
              <select
                className={FIELD}
                value={editing.status ?? "active"}
                onChange={(e) => setEditing({ ...editing, status: e.target.value as SubscriberStatus })}
              >
                <option value="active">Active</option>
                <option value="unsubscribed">Unsubscribed</option>
                <option value="bounced">Bounced</option>
              </select>
            </label>
            <label className="block">
              <span className={LABEL}>Notes</span>
              <textarea
                className={`${FIELD} min-h-[80px]`}
                value={editing.notes ?? ""}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              />
            </label>
            <button type="submit" className={`${BTN} bg-black text-white`} disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save subscriber"}
            </button>
          </form>
        </Panel>
      )}

      {detailId && (
        <Panel title="Subscriber" onClose={() => setDetailId(null)}>
          {detailQuery.isLoading && <p className="text-sm text-black/50">Loading…</p>}
          {detailQuery.data && (
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-base text-black">{detailQuery.data.subscriber.email}</p>
                <p className="text-black/60">
                  {[detailQuery.data.subscriber.fullName, detailQuery.data.subscriber.company]
                    .filter(Boolean)
                    .join(" · ") || "No profile details"}
                </p>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {[
                  ["Sent", detailQuery.data.totals.sent],
                  ["Opened", detailQuery.data.totals.opened],
                  ["Clicked", detailQuery.data.totals.clicked],
                  ["Failed", detailQuery.data.totals.failed],
                ].map(([l, v]) => (
                  <div key={String(l)} className="border border-black/10 p-2 text-center">
                    <p className={LABEL}>{l}</p>
                    <p className="text-lg text-black">{String(v)}</p>
                  </div>
                ))}
              </div>
              <div>
                <p className={LABEL}>Delivery history</p>
                {detailQuery.data.deliveries.length === 0 ? (
                  <p className="mt-2 text-black/50">No campaign deliveries yet.</p>
                ) : (
                  <ul className="mt-2 space-y-2">
                    {detailQuery.data.deliveries.map((d) => (
                      <li key={d.id} className="border-b border-black/5 pb-2">
                        <p className="text-black">{d.campaignName || d.subject}</p>
                        <p className="text-[12px] text-black/50">
                          {d.status} · {fmt(d.sentAt)}
                          {d.openedAt ? " · opened" : ""}
                          {d.clickedAt ? " · clicked" : ""}
                        </p>
                        {d.error && <p className="text-[12px] text-red-700">{d.error}</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
